import { ReactNode, useEffect, useState } from 'react';
import { Confirm, useDataProvider, useGetOne, useNotify, useRecordContext, useRefresh } from 'react-admin';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { __t } from '../../i18n/t';
import { DevRequest, DevRequestAction } from '../../models/devRequest';
import { createPasskey, getPasskey, passkeySupported } from '../../lib/webauthn';
import { SIGNED_ACTIONS } from './actions';
import { actionDoneMessage } from './labels';

export const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 1100 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
    {children}
  </Paper>
);

// RaLayout-root é fit-content: tabela larga só rola aqui dentro se o contêiner não entrar na conta da largura.
export const Scroll = ({ children }: { children: ReactNode }) => (
  <Box sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>{children}</Box>
);

export const Muted = ({ text }: { text: string }) => <Typography variant="body2" color="text.secondary">{text}</Typography>;

/** Texto corrido do robô ou do suporte, preservando as quebras de linha. */
export const Prose = ({ text }: { text?: string | null }) => (
  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
);

export const useCopy = () => {
  const notify = useNotify();
  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(__t('Copiado.'), { type: 'info' });
    } catch {
      notify(__t('Não foi possível copiar.'), { type: 'warning' });
    }
  };
};

/** Bloco monoespaçado com botão copiar. */
export const CopyBlock = ({ text }: { text: string }) => {
  const copy = useCopy();
  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={__t('Copiar')}>
        <IconButton size="small" onClick={() => copy(text)} sx={{ position: 'absolute', top: 4, right: 4 }}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{ m: 0, p: 1.5, pr: 5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 360 }}
      >
        {text}
      </Box>
    </Box>
  );
};

/** Dialog com um campo de texto (resposta, comentário de reabertura, motivo). */
export const TextDialog = (
  { title, label, required, confirmLabel, busy, onClose, onConfirm }:
  { title: string; label: string; required: boolean; confirmLabel: string; busy: boolean; onClose: () => void; onConfirm: (text: string) => void },
) => {
  const [text, setText] = useState('');
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField autoFocus multiline minRows={3} fullWidth margin="dense" label={label} value={text} onChange={e => setText(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{__t('Cancelar')}</Button>
        <Button variant="contained" disabled={busy || (required && !text.trim())} onClick={() => onConfirm(text.trim())}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Cancelar a confirmação não é erro; o resto usa a mensagem do backend. */
const friendlyError = (e: any, fallback: string): string => {
  if (e?.name === 'NotAllowedError') { return __t('Confirmação cancelada.'); }
  return e?.body?.message || e?.message || fallback;
};

/** Esta conta libera pendências? Envia ao robô? Tem digital ou PIN? O backend confere de novo a cada ação. */
export const useApprover = () =>
  useGetOne<{ id: string; isApprover: boolean; isSender: boolean; hasPasskey: boolean }>('dev-requests', { id: 'approver-status' }).data;

const UNSUPPORTED = () => __t('Este navegador não aceita digital nem PIN. Use Chrome, Edge ou Safari atualizados.');

/** Cadastro da digital ou do PIN. Sozinho não libera nada: o SQL de dev_request_approver ou dev_request_sender é que fixa a chave. */
export const RegisterPasskeyButton = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const register = async () => {
    if (!passkeySupported()) {
      notify(UNSUPPORTED(), { type: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const { data: start } = await dataProvider.create('dev-requests/passkeys/options', { data: {} });
      const response = await createPasskey(start.options);
      await dataProvider.create('dev-requests/passkeys', { data: { challengeId: start.challengeId, response, deviceName: navigator.platform } });
      notify(__t('Digital ou PIN cadastrado.'), { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(friendlyError(e, __t('Não foi possível cadastrar a digital ou o PIN.')), { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };
  return <Button size="small" variant="contained" disabled={busy} onClick={register}>{__t('Cadastrar digital ou PIN')}</Button>;
};

/** Manda uma ação do pedido (POST dev-requests/<id>/actions/<action>), avisa e recarrega a tela. */
export const useDevAction = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext<DevRequest>();
  const [busy, setBusy] = useState(false);
  const run = async (action: DevRequestAction, data: { text?: string; diffSha?: string | null } = {}): Promise<boolean> => {
    if (!record || busy) { return false; }
    if (SIGNED_ACTIONS.includes(action) && !passkeySupported()) {
      notify(UNSUPPORTED(), { type: 'warning' });
      return false;
    }
    setBusy(true);
    try {
      if (SIGNED_ACTIONS.includes(action)) {
        const path = `dev-requests/${record.id}/dev-actions/${action}`;
        const { data: start } = await dataProvider.create(`${path}/challenge`, { data });
        const assertion = await getPasskey(start.options);
        await dataProvider.create(path, { data: { ...data, challengeId: start.challengeId, assertion } });
      } else {
        await dataProvider.create(`dev-requests/${record.id}/actions/${action}`, { data });
      }
      notify(actionDoneMessage(action), { type: 'success' });
      refresh();
      return true;
    } catch (e: any) {
      notify(friendlyError(e, __t('Não foi possível concluir a ação.')), { type: 'warning' });
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { run, busy };
};

type Pending = { action: DevRequestAction; title: string; label: string; required: boolean; confirmLabel: string } | null;

/** Botão que abre o TextDialog e, ao confirmar, dispara a ação com `text`. */
export const TextActionButton = (
  { action, buttonLabel, title, label, required, confirmLabel, color = 'primary', variant = 'outlined', run, busy }:
  {
    action: DevRequestAction; buttonLabel: string; title: string; label: string; required: boolean; confirmLabel: string;
    color?: 'primary' | 'error' | 'success'; variant?: 'outlined' | 'contained';
    run: ReturnType<typeof useDevAction>['run']; busy: boolean;
  },
) => {
  const [open, setOpen] = useState<Pending>(null);
  return (
    <>
      <Button size="small" color={color} variant={variant} disabled={busy} onClick={() => setOpen({ action, title, label, required, confirmLabel })}>
        {buttonLabel}
      </Button>
      {open && (
        <TextDialog
          {...open}
          busy={busy}
          onClose={() => setOpen(null)}
          onConfirm={async text => { if (await run(open.action, { text })) { setOpen(null); } }}
        />
      )}
    </>
  );
};

/** Botão com confirmação simples. */
export const ConfirmActionButton = (
  { buttonLabel, title, content, color = 'primary', variant = 'outlined', busy, onConfirm, autoOpen = false, onAutoOpen }:
  {
    buttonLabel: string; title: string; content: ReactNode; color?: 'primary' | 'error' | 'success'; variant?: 'outlined' | 'contained';
    busy: boolean; onConfirm: () => Promise<boolean>; autoOpen?: boolean; onAutoOpen?: () => void;
  },
) => {
  const [open, setOpen] = useState(false);
  // Abre sozinho uma vez (ex.: logo depois de salvar); onAutoOpen limpa o pedido para não reabrir no F5.
  useEffect(() => {
    if (autoOpen) { setOpen(true); onAutoOpen?.(); }
  }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Button size="small" color={color} variant={variant} disabled={busy} onClick={() => setOpen(true)}>{buttonLabel}</Button>
      <Confirm
        isOpen={open}
        loading={busy}
        title={title}
        content={content}
        onClose={() => setOpen(false)}
        onConfirm={async () => { await onConfirm(); setOpen(false); }}
      />
    </>
  );
};
